<template>
  <el-tabs
    class="microsite-editor"
    :value="activeTab"
    :before-leave="handleBeforeTabLeave"
    @tab-click="handleTabClick"
  >
    <el-tab-pane
      :name="PAGE_TAB.NAME"
      :label="$t('admin.crm.label.microsite.micrositeName')"
    >
      <Editor
        class="name-editor"
        ref="nameEditor"
        no-header
        :loading.sync="loading"
        :model="microsite"
        :formLines="formLines"
        @cancel="handleCancel"
        @save="handleSave"
      >
        <template #visibility="{ dataKey, placeholder }">
          <el-select
            v-model="microsite[dataKey]"
            :placeholder="placeholder"
          >
            <el-option
              v-for="(item, index) in visibilityList"
              :key="index"
              :label="$t(`admin.crm.micrositeVisibility.${item}`)"
              :value="item"
            ></el-option>
          </el-select>
        </template>
        <template #group="{dataKey, placeholder}">
          <el-select
            v-model="microsite[dataKey]"
            clearable
            popper-class="group-select"
            :placeholder="placeholder"
          >
          <el-option
            v-for="(item, index) in groupList"
            :key="index"
            :label="item.i18nName"
            :value="item.id"
          ></el-option>
        </el-select>
        </template>
        <template #enableChatbot="{ dataKey, placeholder }">
          <el-select
            v-model="microsite[dataKey]"
            :placeholder="placeholder"
          >
            <el-option
              :label="$t('admin.crm.chatbot.on')"
              :value="enableChatbotValue.on"
            ></el-option>
            <el-option
              :label="$t('admin.crm.chatbot.off')"
              :value="enableChatbotValue.off"
            ></el-option>
          </el-select>
        </template>
      </Editor>
    </el-tab-pane>
    <el-tab-pane
      v-if="isEditMode"
      :name="PAGE_TAB.SET"
      :label="$t('admin.crm.btn.microsite.setMicrosite')"
    >
      <List
        no-head
        no-pagination
        :loading.sync="loading"
        :listData="setList"
        :tableColumns="tableColumns"
        :actionBtn="actionBtn"
      ></List>
    </el-tab-pane>
    <el-tab-pane
      v-if="isEditMode"
      :name="PAGE_TAB.SHARE"
      :label="$t('admin.crm.label.campaign.shareConfigHeader')"
    >
      <div v-rect-loading="loading" class="share-config-container">
        <div class="preview-language">
          <div
            v-for="(item, index) in previewLocaleList"
            class="language-item"
            :key="index"
            :class="item.dataKey === shareLocale && 'is-active'"
            @click="handlePreviewLanguage(item)"
          >{{ item.label }}</div>
        </div>
        <div class="container-header">
          <div class="title">{{ $t('admin.crm.label.campaign.shareConfigHeader') }}</div>
          <el-switch v-model="weChatShareEnabled" class="config-status-switch" :width="34"></el-switch>
        </div>
        <div class="tip-message">
          {{ $t('admin.crm.message.microsite.shareTip') }}
        </div>
        <div class="share-config">
          <div class="share-image config-line">
            <span class="config-label">{{ $t('admin.crm.label.microsite.shareImage') }}</span>
            <div class="image-container" :class="!weChatShareConfig.imageUrl && 'no-image'">
              <img :src="weChatShareConfig.imageUrl"/>
              <div class="add-btn" @click="handleAddImageBtnClick">
                <span class="btn-plus">+</span>
                <span class="btn-label">{{ $t('admin.crm.btn.addImage') }}</span>
              </div>
              <input
                ref="shareImageFile"
                type="file"
                :accept="acceptFileType.join(',')"
                @change="handleImageFileChange(false)"
              />
            </div>
          </div>
          <div class="share-friends-title config-line">
            <span class="config-label">{{ $t('admin.crm.placeholder.microsite.shareFriendsTitle') }}</span>
            <el-input v-model="weChatShareConfig.friendsTitle"></el-input>
          </div>
          <div class="share-description config-line">
            <span class="config-label">{{ $t('admin.crm.placeholder.microsite.shareDesc') }}</span>
            <el-input
              v-model="weChatShareConfig.description"
              type="textarea"
              :autosize="{ minRows: 3 }"
            ></el-input>
          </div>
          <div class="share-moments-title config-line">
            <span class="config-label">{{ $t('admin.crm.placeholder.microsite.shareMomentsTitle') }}</span>
            <el-input v-model="weChatShareConfig.momentsTitle"></el-input>
          </div>
        </div>
        <div class="bottom-btn-group">
          <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
          <el-button class="save-btn" @click="handleSave()">{{ $t('common.btn.save') }}</el-button>
        </div>
      </div>
    </el-tab-pane>
  </el-tabs>
</template>

<script>
import _ from 'lodash'
import * as qiniu from 'qiniu-js'
import { mapActions, mapGetters } from 'vuex'
import { MICROSITE_PATH, ACCEPT_FILE_TYPE, ACCEPT_FILE_SIZE, MICROSITE_VISIBILITY_LIST } from '@/constant/crm'
import { addI18nProperty, isEn } from '@/i18n'
import Editor from '../editor.vue'
import List from '@/components/tableList/allowDragSortTable.vue'

const PAGE_TAB = {
  NAME: 'name',
  SET: 'set',
  SHARE: 'share'
}

export default {
  components: {
    Editor,
    List
  },
  metaInfo () {
    return {
      style: [
        { innerHTML: '.el-select-dropdown.el-popper.group-select{max-width: 486px;}' }
      ]
    }
  },
  data () {
    const vm = this
    return {
      PAGE_TAB,
      loading: false,
      groupList: [],
      microsite: {
        visibility: 'IE',
        enableChatbot: false
      },
      visibilityList: MICROSITE_VISIBILITY_LIST,
      enableChatbotValue: {
        on: true,
        off: false
      },
      setList: [
        { id: 'mobileCnLink' },
        { id: 'mobileEnLink' },
        { id: 'webCnLink' },
        { id: 'webEnLink' }
      ],
      tableColumns: [
        {
          dataKey: 'id',
          labelI18nKey: 'admin.crm.label.microsite.pcOrMobile',
          formatter: (id) => this.$t(`admin.crm.label.microsite.${id}`)
        }
      ],
      actionBtn: {
        edit: {
          labelI18nKey: 'admin.crm.btn.microsite.setMicrosite',
          onclick: this.handleEdit
        },
        preview: {
          labelI18nKey: 'admin.crm.btn.microsite.preview',
          onclick: this.handlePreview
        }
      },
      previewLocaleList: [
        {
          dataKey: 'cn',
          label: '简体中文'
        },
        {
          dataKey: 'en',
          label: 'English'
        }
      ],
      shareLocale: '',
      acceptFileType: ACCEPT_FILE_TYPE,
      imageUploader: {

      },
      weChatShareEnabled: false,
      weChatShareConfig: {
        get imageUrl () {
          const locale = vm.shareLocale
          if (locale) {
            const path = this[locale].imageUrl || null
            if (!path || path.includes('http')) {
              return path
            } else {
              return `${process.env.VUE_APP_QINIU_DOMAIN}${path}`
            }
          }
          return null
        },
        get friendsTitle () {
          const locale = vm.shareLocale
          if (locale) {
            return this[locale].friendsTitle
          }
          return null
        },
        get description () {
          const locale = vm.shareLocale
          if (locale) {
            return this[locale].description
          }
          return null
        },
        get momentsTitle () {
          const locale = vm.shareLocale
          if (locale) {
            return this[locale].momentsTitle
          }
          return null
        },
        set imageUrl (value) {
          const locale = vm.shareLocale
          if (locale) {
            this[locale].imageUrl = value
          }
        },
        set friendsTitle (value) {
          const locale = vm.shareLocale
          if (locale) {
            this[locale].friendsTitle = value
          }
        },
        set description (value) {
          const locale = vm.shareLocale
          if (locale) {
            this[locale].description = value
          }
        },
        set momentsTitle (value) {
          const locale = vm.shareLocale
          if (locale) {
            this[locale].momentsTitle = value
          }
        },
        en: {
          imageUrl: '',
          friendsTitle: '',
          description: '',
          momentsTitle: ''
        },
        cn: {
          imageUrl: '',
          friendsTitle: '',
          description: '',
          momentsTitle: ''
        }
      }
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    activeTab () {
      const tab = this.$route.query.tab
      if (!Object.values(PAGE_TAB).includes(tab)) {
        return PAGE_TAB.NAME
      }
      return tab
    },
    formLines () {
      const lines = {
        name: {
          labelI18nKey: 'admin.crm.label.microsite.micrositeName',
          placeholderI18nKey: 'admin.crm.placeholder.microsite.micrositeName',
          rules: [{ required: true, message: this.$t('admin.crm.message.microsite.micrositeName') }]
        },
        visibility: {
          labelI18nKey: 'admin.crm.label.microsite.visibility'
        },
        group: {
          labelI18nKey: 'admin.crm.label.microsite.group',
          placeholderI18nKey: 'admin.crm.placeholder.microsite.group'
        },
        enableChatbot: {
          hidden: !this.hasAnyPermission(['COMPUTED_COMPANY_HAS_CHAT_BOT', 'PERM_CHATBOT_MANAGEMENT']),
          labelI18nKey: 'admin.crm.label.microsite.chatbot'
        }
      }
      return lines
    }
  },
  beforeCreate () {
    const tab = this.$route.query.tab
    if (!Object.values(PAGE_TAB).includes(tab)) {
      this.$router.replace({ query: { ...this.$route.query, tab: PAGE_TAB.NAME } })
    }
  },
  created () {
    this.shareLocale = isEn() ? 'en' : 'cn'
    this.loading = true
    const promiseList = [this.getGroupList()]
    if (this.isEditMode) {
      promiseList.push(this.getMicrosite())
    }
    Promise.all(promiseList).finally(() => {
      this.loading = false
    })
  },
  methods: {
    ...mapActions('crm', ['getMicrositeById', 'createMicrosite', 'updateMicrosite', 'getMicrositeGroupList']),
    ...mapActions('common', ['getQiniuUploadToken']),
    ...mapGetters('account', ['getCurrentAccount']),
    handleTabClick (pane) {
      const tabName = pane.paneName
      if (tabName === this.activeTab) {
        return
      }
      if (this.handleBeforeTabLeave()) {
        this.$router.push({ query: { ...this.$route.query, tab: tabName } })
      }
    },
    handleBeforeTabLeave (newTabName) {
      if (this.activeTab !== PAGE_TAB.NAME) {
        return true
      }
      let result = true
      this.$refs.nameEditor.validate((isSuccess) => {
        result = isSuccess
      })
      return result
    },
    async getGroupList () {
      const resp = await this.getMicrositeGroupList()
      if (resp) {
        this.groupList = _.map(resp.list, group => addI18nProperty(group, 'i18nName', 'cn_name', 'name'))
      }
    },
    async getMicrosite () {
      this.loading = true
      try {
        const micrositeId = this.$route.params.micrositeId
        const resp = await this.getMicrositeById(micrositeId)
        this.renderMicorsite(resp)
      } finally {
        this.loading = false
      }
    },
    renderMicorsite (resp) {
      if (resp) {
        this.microsite = {
          id: resp.id,
          name: resp.name,
          group: resp.group.id,
          short_unique_id: resp.short_unique_id,
          webEnLink: `${MICROSITE_PATH.EDIT}${resp.short_unique_id}/pc/en/crm/`,
          webCnLink: `${MICROSITE_PATH.EDIT}${resp.short_unique_id}/pc/zh-cn/crm/`,
          mobileEnLink: `${MICROSITE_PATH.EDIT}${resp.short_unique_id}/mobile/en/crm/`,
          mobileCnLink: `${MICROSITE_PATH.EDIT}${resp.short_unique_id}/mobile/zh-cn/crm/`,
          indexUrl: `${MICROSITE_PATH.INDEX}${resp.short_unique_id}`,
          link: `${window.location.origin}${MICROSITE_PATH.INDEX}${resp.short_unique_id}`,
          visibility: resp.visibility,
          enableChatbot: resp.enable_chatbot
        }
        this.weChatShareEnabled = resp.wechat_share_enabled
        const weChatShareConfig = JSON.parse(resp.wechat_share_config) || {}
        this.weChatShareConfig.en = weChatShareConfig.en
        this.weChatShareConfig.cn = weChatShareConfig.cn
      }
    },
    handleCancel () {
      this.$router.push({ name: 'micrositeList' })
    },
    async handleSave (closeLoading) {
      const weChatShareConfig = {
        en: this.weChatShareConfig.en,
        cn: this.weChatShareConfig.cn
      }
      const enImage = this.imageUploader.en
      const cnImage = this.imageUploader.cn
      if (enImage) {
        weChatShareConfig.en.imageUrl = await this.uploadFileToCDN(enImage)
      }
      if (cnImage) {
        weChatShareConfig.cn.imageUrl = await this.uploadFileToCDN(cnImage)
      }

      const micrositeDetail = {
        name: this.microsite.name,
        group: this.microsite.group,
        visibility: this.microsite.visibility,
        enable_chatbot: this.microsite.enableChatbot,
        wechat_share_enabled: this.weChatShareEnabled,
        wechat_share_config: JSON.stringify(weChatShareConfig)
      }
      let saveFunction = null
      if (this.isEditMode) {
        micrositeDetail.id = this.microsite.id
        saveFunction = this.updateMicrosite
      } else {
        saveFunction = this.createMicrosite
      }
      const resp = await saveFunction(micrositeDetail)
      if (resp) {
        this.$message.success(this.$t('common.message.saveSuccess'))
        if (!this.isEditMode) {
          this.$router.push({
            name: 'editMicrosite',
            params: {
              micrositeId: resp.id
            },
            query: {
              ...this.$route.query,
              tab: PAGE_TAB.SET
            }
          })
          this.renderMicorsite(resp)
        }
        closeLoading && closeLoading()
      } else {
        this.$message({ message: this.$t('admin.crm.message.microsite.requestError'), type: 'error' })
      }
    },
    handleEdit (rowData) {
      window.open(this.microsite[rowData.id])
    },
    handlePreview () {
      window.open(this.microsite.indexUrl)
    },
    handlePreviewLanguage (item) {
      this.shareLocale = item.dataKey
    },
    handleAddImageBtnClick () {
      this.$refs.shareImageFile.click()
    },
    handleImageFileChange () {
      const fileInput = this.$refs.shareImageFile
      const file = fileInput.files[0]
      if (!file) {
        return
      }
      if (!ACCEPT_FILE_TYPE.includes(file.type)) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.$t('admin.crm.message.microsite.wrongImageType')
        })
        fileInput.value = null
        return
      }
      if (file.size > ACCEPT_FILE_SIZE) {
        this.$message.error({
          type: 'error',
          showClose: true,
          message: this.$t('admin.crm.message.microsite.wrongImageSize', { size: 2 })
        })
        fileInput.value = null
        return
      }
      this.imageUploader[this.shareLocale] = file
      this.weChatShareConfig.imageUrl = window.URL.createObjectURL(file)
    },
    async uploadFileToCDN (file) {
      const fileKey = `/crm/microsite/${this.microsite.id}/${this.getCurrentAccount().id}/${new Date().valueOf()}`
      const tokenResp = await this.getQiniuUploadToken({ is_public: true })
      const uploadToken = tokenResp.token
      return new Promise((resolve, reject) => {
        const observable = qiniu.upload(file, fileKey, uploadToken, {}, { region: qiniu.region.z0 })
        observable.subscribe(
          (resp) => {
            // in progress
          },
          (resp) => {
            // error
            if (resp && resp.message) {
              this.$message.error({
                type: 'error',
                showClose: true,
                message: this.$t('admin.crm.message.microsite.uploadError')
              })
              this.uploader.token = null
            }
            reject(resp)
          },
          (resp) => {
            // complete
            resolve(fileKey)
          }
        )
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.microsite-editor {
  .name-editor {
    ::v-deep .form-container {
      margin-top: 15px;
    }
  }

  .share-config-container {
    width: 600px;
    margin: 15px auto 0;

    .preview-language {
      text-align: right;

      .language-item {
        display: inline-block;
        color: #0096cc;
        vertical-align: middle;
        line-height: 1;
        cursor: pointer;

        &.is-active {
          color: #303133;
          cursor: default;
        }

        &:last-child {
          margin-left: 12px;
          padding-left: 12px;
          border-left: 1px solid #ddd;
        }
      }
    }

    .container-header {
      margin-top: 20px;
      position: relative;

      .title {
        line-height: 22px;
        color: #303133;
        font-size: 16px;
        font-weight: 500;
      }

      .config-status-switch {
        display: flex;
        position: absolute;
        right: 0;
        top: 1px;

        ::v-deep {
          &.is-checked  {
            .el-switch__core {
              &::after {
                margin-left: -15px;
              }
            }
          }
          .el-switch__core {
            height: 16px;

            &::after {
              width: 14px;
              height: 14px;
              top: 0;
            }
          }
        }
      }
    }

    .tip-message {
      margin-top: 16px;
    }

    .share-config {
      margin-top: 20px;

      .config-line {
        display: flex;

        .config-label {
          display: inline-block;
          width: 114px;
          margin-right: 12px;
          vertical-align: middle;
          line-height: 32px;
          flex-grow: 0;
          flex-shrink: 0;
          text-align: right;
        }
      }

      .share-image {
        .config-label {
          line-height: 100px;
        }

        .image-container {
          width: 100px;
          height: 100px;
          border-radius: 6px;
          overflow: hidden;
          vertical-align: middle;
          position: relative;

          &:not(.no-image):hover {
            .add-btn {
              display: block;
              background: rgba($color: #ffffff, $alpha: 0.75);
            }
          }

          &.no-image {
            .add-btn {
              display: block;
            }

            img {
              visibility: hidden;
            }
          }

          .add-btn {
            display: none;
            width: 100%;
            height: 100%;
            border: 1px dashed #0096cc;
            border-radius: 6px;
            text-align: center;
            color: #606266;
            background: #fafafa;
            cursor: pointer;
            position: absolute;
            top: 0;
            left: 0;

            .btn-plus {
              display: block;
              margin: 20px 0 14px;
              font-size: 22px;
            }

            .btn-label {
              font-size: 12px;
              line-height: 17px;
            }
          }

          img {
            display: block;
            width: 100%;
            height: 100%;
          }

          input {
            display: none;
            visibility: hidden;
          }
        }

      }

      ::v-deep {
        .el-input__inner {
          height: 32px;
          line-height: 32px;
          border-radius: 2px;
        }

        .el-textarea__inner {
          padding: 5px 15px;
          border-radius: 2px;
        }
      }
      .share-friends-title {
        margin-top: 14px;
      }

      .share-description {
        margin-top: 20px;
      }

      .share-moments-title {
        margin-top: 20px;
      }
    }
    .config-body {
      .title-container {
        display: none;
      }

    }
  }

  .bottom-btn-group {
    display: flex;
    margin-top: 40px;
    justify-content: flex-end;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
.group-select {
  .el-select-dropdown__item {
    padding: 0 10px;
  }
}
.page-en {
  .microsite-editor {
    .share-config-container {
      .share-config {
        .config-line {
          .config-label {
            width: 164px;
          }
        }
      }
    }
  }
}
</style>
